// Toque seguro con Web NFC (design/SECURE-CARD.md, fases A-C).
//
// Web NFC solo LEE y ESCRIBE mensajes NDEF; no puede abrir una sesion ISO-DEP ni leer paginas en bruto. De ahi:
//   - NTAG 424 DNA: la tarjeta expone su URL SDM como registro NDEF (`https://securegatecard.nordicbyte.co.uk/t?c=..&p=..&m=..`).
//     Se manda tal cual a POST /api/auth/card, como hace la app (`chipType: 'ntag424'`, `sdm: <url>`).
//   - NTAG 215/216: el formato SecureGate es un registro NDEF de tipo externo `nordicbyte.co.uk:sgc` que se puede leer,
//     pero el testigo rodante se reescribe tras cada login en paginas protegidas con la contrasena de 32 bits del chip
//     (PWD_AUTH), y Web NFC no puede hacer PWD_AUTH ni escribir paginas. Sin reescribir el testigo la deteccion de
//     copias queda anulada, asi que el panel NO inicia sesion con estas tarjetas: lo dice y remite a la app. Y no
//     llama al servidor (cada login valido emitiria un testigo pendiente que nadie confirmaria).
//
// Todo lo de aqui es puro (sin React) para poder probarlo con falsos: no hay NFC en la maquina de pruebas.

export const SGC_EXTERNAL_TYPE = 'nordicbyte.co.uk:sgc';
export const SDM_HOST = 'securegatecard.nordicbyte.co.uk';

/** Resultado de clasificar una lectura NDEF. */
export const TapKind = Object.freeze({
  NTAG424: 'ntag424', // URL SDM valida: se puede iniciar sesion
  NTAG21X: 'ntag21x', // formato SecureGate de NTAG 215/216: solo en la app
  PLAIN: 'plain', // una etiqueta sin formato SecureGate: login por UID de siempre
  UNKNOWN: 'unknown', // apunta a nuestro host pero no es una URL SDM valida, o no hay nada util
});

/** ¿Soporta este navegador Web NFC? (Chrome en Android; en escritorio, iOS y Firefox no.) */
export function webNfcSupported(win = typeof window !== 'undefined' ? window : undefined) {
  return !!win && 'NDEFReader' in win;
}

/** ¿Es una URL SDM de SecureGate? Mismo criterio que `isSecureGateSdmUrl` de la app (el servidor normaliza a mayusculas). */
export function isSecureGateSdmUrl(url) {
  if (typeof url !== 'string' || !url) return false;
  let u;
  try { u = new URL(url); } catch { return false; }
  if (u.protocol !== 'https:' || u.hostname !== SDM_HOST || u.pathname !== '/t') return false;
  const hex = (name, len) => new RegExp(`^[0-9A-Fa-f]{${len}}$`).test(u.searchParams.get(name) || '');
  return hex('c', 16) && hex('p', 32) && hex('m', 16);
}

/** Texto de un registro NDEF de Web NFC (`data` es un DataView; en las pruebas puede ser un string). */
export function decodeRecordText(record) {
  const data = record?.data;
  if (typeof data === 'string') return data;
  if (!data) return '';
  try {
    return new TextDecoder(record.encoding || 'utf-8').decode(data);
  } catch {
    return '';
  }
}

function normalizeSerial(serialNumber) {
  return String(serialNumber || '').replace(/:/g, '').toUpperCase();
}

/**
 * Clasifica lo que leyo `NDEFReader` (`event.message.records`, `event.serialNumber`).
 * Primero busca el formato SecureGate (que gana al UID aunque el chip tenga ademas otros registros); solo si no hay
 * nada nuestro devuelve `plain` con el UID.
 */
export function classifyNdefRead({ records, serialNumber } = {}) {
  const list = Array.from(records || []);
  const uid = normalizeSerial(serialNumber);

  if (list.some((r) => r?.recordType === SGC_EXTERNAL_TYPE)) return { kind: TapKind.NTAG21X, uid };

  let sawOurHost = false;
  for (const r of list) {
    if (r?.recordType !== 'url' && r?.recordType !== 'absolute-url') continue;
    const url = decodeRecordText(r);
    if (isSecureGateSdmUrl(url)) return { kind: TapKind.NTAG424, uid, url };
    try { if (new URL(url).hostname === SDM_HOST) sawOurHost = true; } catch { /* no es una URL */ }
  }
  if (sawOurHost) return { kind: TapKind.UNKNOWN, uid };
  if (uid) return { kind: TapKind.PLAIN, uid };
  return { kind: TapKind.UNKNOWN, uid };
}

/**
 * Cuerpo de `POST /api/auth/card` para una lectura de NTAG 424 DNA, o el reintento con PIN con `tapTicket`.
 * Con `tapTicket` NO se reenvia la URL: su contador ya se consumio en la primera presentacion y el servidor la
 * rechazaria como repetida (por eso existe el ticket).
 */
export function buildSecureTapBody({ read, tapTicket, pin, masterPassword, trustTokens, rememberDevice, language }) {
  const body = {};
  if (tapTicket) {
    body.tapTicket = tapTicket;
  } else {
    if (!read || read.kind !== TapKind.NTAG424) throw new Error('secure tap needs an NTAG 424 DNA read');
    body.chipType = 'ntag424';
    if (read.uid) body.uid = read.uid;
    body.sdm = read.url;
  }
  if (pin) body.pin = pin;
  if (masterPassword) body.masterPassword = masterPassword;
  body.trustTokens = trustTokens || [];
  body.rememberDevice = !!rememberDevice;
  if (language) body.language = language;
  return body;
}

/**
 * Una lectura Web NFC: resuelve con la primera lectura NDEF (`{ records, serialNumber }`) y se puede cancelar.
 * `ReaderCtor` es `NDEFReader` (inyectable para las pruebas). Cancelar (o resolver) aborta el `scan`: el codigo
 * anterior dejaba el lector abierto y acumulaba lectores con cada pulsacion.
 */
export function scanNdefOnce(ReaderCtor) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let cancelImpl = () => {};
  const promise = new Promise((resolve, reject) => {
    let done = false;
    const finish = (fn, value) => {
      if (done) return;
      done = true;
      try { controller?.abort(); } catch { /* ya abortado */ }
      fn(value);
    };
    cancelImpl = () => finish(reject, Object.assign(new Error('cancelled'), { name: 'AbortError' }));
    let reader;
    try {
      reader = new ReaderCtor();
    } catch (err) {
      finish(reject, err);
      return;
    }
    reader.onreading = (event) => finish(resolve, {
      records: Array.from(event?.message?.records || []),
      serialNumber: event?.serialNumber,
    });
    reader.onreadingerror = () => finish(reject, new Error('nfc_read_error'));
    Promise.resolve()
      .then(() => reader.scan(controller ? { signal: controller.signal } : undefined))
      .catch((err) => finish(reject, err));
  });
  return { promise, cancel: () => cancelImpl() };
}
